import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../lib/i18n/LanguageContext';
import { useLocalizedName, useLocalizedDescription } from '../../lib/utils/formatters';
import { fetchWineCategories, fetchWineItems } from '../../lib/supabase/api';
import type { WineCategory, WineItemWithCategory } from '../../lib/supabase/types';
import { usePageNavigation } from '../../lib/contexts/PageNavigationContext';
import SEO from '../../components/SEO';

// Import modular components
import WineListLoadingError from './WineListLoadingError';
import WineListContent from './WineListContent';
import WineListPairing from './WineListPairing';
import CategorySubNav from '../../components/ui/CategorySubNav';

const WineList = () => {
  const { t } = useLanguage();
  const getLocalizedName = useLocalizedName();
  const getLocalizedDescription = useLocalizedDescription();
  const [wineCategories, setWineCategories] = useState<WineCategory[]>([]);
  const [wineItems, setWineItems] = useState<WineItemWithCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { setCurrentPageType, setCategories: setPageCategories } = usePageNavigation();

  // Create stable slugs for categories
  const categoriesWithSlugs = wineCategories.map(category => ({
    ...category,
    slug: category.name.toLowerCase().replace(/\s+/g, '-'),
  }));

  // Group wine items by category
  const groupedWineItems = categoriesWithSlugs.reduce(
    (acc, category) => {
      acc[category.slug] = wineItems.filter(item => item.wine_category_id === category.id);
      return acc;
    },
    {} as Record<string, WineItemWithCategory[]>
  );

  // Special grouping for 'Beverages' sub-categories
  const beveragesCategory = categoriesWithSlugs.find(cat => cat.name === 'Beverages');
  let groupedBeverageSubCategories: Record<string, WineItemWithCategory[]> = {};

  if (beveragesCategory) {
    const beverageItems = wineItems.filter(item => item.wine_category_id === beveragesCategory.id);
    groupedBeverageSubCategories = beverageItems.reduce(
      (acc, item) => {
        if (item.sub_category) {
          if (!acc[item.sub_category]) {
            acc[item.sub_category] = [];
          }
          acc[item.sub_category].push(item);
        }
        return acc;
      },
      {} as Record<string, WineItemWithCategory[]>
    );
  }

  useEffect(() => {
    window.scrollTo(0, 0);

    // Set page type for navigation context
    setCurrentPageType('wine-list');

    // Cleanup when leaving the page
    return () => {
      setCurrentPageType(null);
      setPageCategories([]);
    };
  }, [t, setCurrentPageType, setPageCategories]);

  // Load wine data
  useEffect(() => {
    const loadWineData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const [categoriesData, wineItemsData] = await Promise.all([
          fetchWineCategories(),
          fetchWineItems(),
        ]);

        setWineCategories(categoriesData);
        setWineItems(wineItemsData);

        // Update page navigation context with categories
        setPageCategories(categoriesData);
      } catch (err) {
        console.error('Failed to load wine data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load wine data');
      } finally {
        setIsLoading(false);
      }
    };

    loadWineData();
  }, [setPageCategories]);

  if (isLoading || error) {
    return <WineListLoadingError isLoading={isLoading} error={error} />;
  }

  // Structured data for wine list page
  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Menu',
      name: t.wineList.hero.title,
      description: t.wineList.description,
      hasMenuSection: categoriesWithSlugs.map(category => ({
        '@type': 'MenuSection',
        name: getLocalizedName(category),
        hasMenuItem: (groupedWineItems[category.slug] || []).map(item => ({
          '@type': 'MenuItem',
          name: getLocalizedName(item),
          description: item.description ? getLocalizedDescription(item) : '',
          offers: {
            '@type': 'Offer',
            price: item.price,
            priceCurrency: 'ISK',
          },
        })),
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://rossopomodoro.is/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Wine List',
          item: 'https://rossopomodoro.is/wine-list',
        },
      ],
    },
  ];
  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="pt-24 pb-32 md:pb-16 bg-neutral-50"
    >
      <SEO
        title={`${t.wineList.hero.title} | Rossopomodoro`}
        description="Curated Italian wine selection at Rossopomodoro Reykjavik. Premium wines from Italy's finest regions. Perfect pairings for Neapolitan pizza & pasta. Expert recommendations."
        canonical="https://rossopomodoro.is/wine-list"
        keywords="Italian wine list Reykjavik, Italian wines, wine pairing, Chianti, Prosecco, Amarone, wine selection, Italian restaurant wines, premium wines"
        ldjson={structuredData}
      />
      <div className="container-custom px-4 md:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto mb-12">
          <h2 className="text-neutral-700 text-center text-lg font-medium mb-4">
            Premium Italian Wine Selection in Reykjavik
          </h2>
          <p className="text-neutral-700 text-center italic mb-4 leading-relaxed">
            {t.wineList.description}
          </p>
          <p className="text-neutral-700 text-center leading-relaxed">
            At Rossopomodoro, the best Italian restaurant in downtown Reykjavik, we take pride in
            our carefully selected wine list. Visit us on Laugavegur to experience authentic Italian
            cuisine paired with exceptional wines.
          </p>
        </div>

        <WineListContent
          categoriesWithSlugs={categoriesWithSlugs}
          groupedWineItems={groupedWineItems}
          groupedBeverageSubCategories={groupedBeverageSubCategories}
        />

        <WineListPairing />
      </div>
      <CategorySubNav />
    </motion.section>
  );
};

export default WineList;
